import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { numberToWords } from "../lib/numwords";

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function NumberToWordsTool() {
  const uid = useId();
  const [input, setInput] = useState("1234");
  const [copied, setCopied] = useState(false);

  const words = useMemo(() => {
    const t = input.trim();
    if (t === "" || !/^-?\d+$/.test(t)) return null;
    const w = numberToWords(Number(t));
    return w === null ? null : capitalize(w);
  }, [input]);

  const copy = async () => {
    if (!words) return;
    try {
      await navigator.clipboard.writeText(words);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1.5 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <Label htmlFor={`${uid}-in`}>{m.nw_input_label()}</Label>
        <Input
          id={`${uid}-in`}
          type="text"
          inputMode="numeric"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={m.nw_placeholder()}
          autoComplete="off"
          className="font-mono text-lg"
        />
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {m.nw_output_label()}
          </h2>
          {words && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copy}
              className="gap-2"
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? m.nw_copied() : m.nw_copy()}
            </Button>
          )}
        </div>
        {words ? (
          <p
            data-testid="nw-output"
            className="text-lg leading-relaxed text-foreground"
          >
            {words}
          </p>
        ) : (
          <p data-testid="nw-error" className="text-sm text-muted-foreground">
            {m.nw_invalid()}
          </p>
        )}
      </div>
    </div>
  );
}
